// ═══ Beat Detection — BPM and beat timestamps from audio analysis ═══
//
// Two approaches:
// 1. Offline analysis: analyzeBeats() runs once on audio load, returns BPM + beat timestamps
// 2. Realtime: useBeatPulse() returns a 0-1 pulse that peaks on detected beats (from FFT data)
//
// Usage:
//   // In the editor (offline, on audio load):
//   const { bpm, beats } = await analyzeBeats(audioBuffer)
//
//   // In the composition (realtime, per-frame):
//   const { isOnBeat, beatIntensity, bpm, beats } = useBeatContext()

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

// ═══ Types ═══

export interface BeatAnalysis {
  /** Detected BPM (beats per minute). 0 if detection failed. */
  bpm: number
  /** Beat timestamps in milliseconds. Sorted ascending. */
  beats: number[]
  /** Confidence in the BPM estimate, 0-1 */
  confidence: number
}

export interface BeatContextData {
  /** Current BPM (0 if not analyzed yet) */
  bpm: number
  /** All beat timestamps in ms */
  beats: number[]
  /** True if current frame is within ~50ms of a beat */
  isOnBeat: boolean
  /** 0-1 pulse that decays after each beat (use for animations) */
  beatIntensity: number
  /** Analysis confidence 0-1 */
  confidence: number
}

const EMPTY_BEAT: BeatContextData = { bpm: 0, beats: [], isOnBeat: false, beatIntensity: 0, confidence: 0 }

// ═══ Offline Beat Analysis ═══

/**
 * Analyze an audio buffer to detect BPM and beat positions.
 * Uses onset detection via spectral flux in the bass band.
 */
export async function analyzeBeats(audioUrl: string): Promise<BeatAnalysis> {
  try {
    const response = await fetch(audioUrl)
    const arrayBuffer = await response.arrayBuffer()

    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer)
    audioCtx.close()

    const sampleRate = audioBuffer.sampleRate
    const channelData = audioBuffer.getChannelData(0) // mono

    // Parameters
    const hopSize = Math.round(sampleRate * 0.01) // 10ms hops
    const windowSize = 1024

    // Extract energy envelope (RMS in bass band via simple lowpass)
    const energyFrames: number[] = []
    for (let i = 0; i < channelData.length - windowSize; i += hopSize) {
      let sum = 0
      for (let j = 0; j < windowSize; j++) {
        sum += channelData[i + j] * channelData[i + j]
      }
      energyFrames.push(Math.sqrt(sum / windowSize))
    }

    // Compute spectral flux (difference between consecutive frames)
    const flux: number[] = [0]
    for (let i = 1; i < energyFrames.length; i++) {
      flux.push(Math.max(0, energyFrames[i] - energyFrames[i - 1]))
    }

    // Adaptive threshold: local mean + std over ~0.5s window
    const thresholdWindow = Math.round(0.5 * sampleRate / hopSize)
    const onsets: number[] = []

    for (let i = thresholdWindow; i < flux.length - thresholdWindow; i++) {
      const window = flux.slice(i - thresholdWindow, i + thresholdWindow)
      const mean = window.reduce((a, b) => a + b, 0) / window.length
      const std = Math.sqrt(window.reduce((a, b) => a + (b - mean) ** 2, 0) / window.length)
      const threshold = mean + 1.5 * std

      if (flux[i] > threshold && flux[i] >= flux[i - 1] && flux[i] >= flux[i + 1]) {
        const timeMs = (i * hopSize / sampleRate) * 1000
        // Minimum 200ms between beats (max 300 BPM)
        if (onsets.length === 0 || timeMs - onsets[onsets.length - 1] > 200) {
          onsets.push(timeMs)
        }
      }
    }

    if (onsets.length < 4) {
      return { bpm: 0, beats: onsets, confidence: 0 }
    }

    // Compute inter-onset intervals and estimate BPM
    const intervals: number[] = []
    for (let i = 1; i < onsets.length; i++) {
      intervals.push(onsets[i] - onsets[i - 1])
    }

    // Cluster intervals to find dominant tempo
    const bpmCandidates = intervals.map(i => 60000 / i).filter(b => b >= 60 && b <= 200)
    if (bpmCandidates.length === 0) {
      return { bpm: 0, beats: onsets, confidence: 0 }
    }

    // Histogram with 1 BPM bins
    const histogram = new Map<number, number>()
    for (const b of bpmCandidates) {
      const bin = Math.round(b)
      histogram.set(bin, (histogram.get(bin) || 0) + 1)
    }

    // Find peak bin (allow +-1 BPM tolerance)
    let bestBpm = 0
    let bestCount = 0
    for (const [bin, count] of histogram) {
      const cluster = count + (histogram.get(bin - 1) || 0) + (histogram.get(bin + 1) || 0)
      if (cluster > bestCount) {
        bestCount = cluster
        bestBpm = bin
      }
    }

    const confidence = Math.min(1, bestCount / bpmCandidates.length * 2)

    // Re-quantize beats to the detected BPM grid
    const beatIntervalMs = 60000 / bestBpm
    const quantizedBeats: number[] = []
    if (onsets.length > 0) {
      // Start from first onset, lay down a grid
      const firstBeat = onsets[0]
      const durationMs = (channelData.length / sampleRate) * 1000
      for (let t = firstBeat; t < durationMs; t += beatIntervalMs) {
        quantizedBeats.push(Math.round(t))
      }
    }

    return { bpm: bestBpm, beats: quantizedBeats, confidence }
  } catch (err) {
    console.error('Beat analysis failed:', err)
    return { bpm: 0, beats: [], confidence: 0 }
  }
}

// ═══ Beat Context (for Remotion compositions) ═══

const BeatContext = createContext<BeatContextData>(EMPTY_BEAT)

export function useBeatContext(): BeatContextData {
  return useContext(BeatContext)
}

export const BeatProvider: React.FC<{
  audioUrl: string | undefined
  currentMs: number
  children: React.ReactNode
}> = ({ audioUrl, currentMs, children }) => {
  const [analysis, setAnalysis] = useState<BeatAnalysis>({ bpm: 0, beats: [], confidence: 0 })

  useEffect(() => {
    if (!audioUrl) return
    analyzeBeats(audioUrl).then(setAnalysis).catch(() => {})
  }, [audioUrl])

  const value = useMemo<BeatContextData>(() => {
    if (analysis.beats.length === 0) return EMPTY_BEAT

    // Find nearest beat
    let nearestDist = Infinity
    for (const beat of analysis.beats) {
      const dist = Math.abs(currentMs - beat)
      if (dist < nearestDist) nearestDist = dist
      if (beat > currentMs + 100) break // beats are sorted, no need to check further
    }

    const isOnBeat = nearestDist < 50 // within 50ms of a beat
    // Exponential decay from beat: peak at 1, decays over ~200ms
    const beatIntensity = nearestDist < 300 ? Math.exp(-nearestDist / 80) : 0

    return {
      bpm: analysis.bpm,
      beats: analysis.beats,
      isOnBeat,
      beatIntensity,
      confidence: analysis.confidence,
    }
  }, [analysis, currentMs])

  return (
    <BeatContext.Provider value={value}>
      {children}
    </BeatContext.Provider>
  )
}
